// Single conversation row in the sidebar. Handles:
//  - Active highlight
//  - Inline rename
//  - Hover ⋮ menu (Rename / Move to Project / Delete + Soon: Duplicate, Archive)
import { useState } from "react";
import { MoreHorizontal, Pencil, Trash2, FolderInput, Copy, Archive, MessageSquare, Wand2, FileText, Brain } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { InlineRename } from "./InlineRename";

const TOOL_ICON = {
  reply_assistant: Wand2,
  proposal_generator: FileText,
  client_analyzer: Brain,
};

export function ConversationItem({
  conv, isActive, projects, onPick, onRename, onMove, onDelete, nested = false,
}) {
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const Icon = TOOL_ICON[conv.tool] || MessageSquare;

  return (
    <div
      className={[
        "group/row relative flex items-center rounded-md transition-colors",
        isActive
          ? "bg-secondary text-foreground"
          : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
        nested ? "pl-6 pr-1" : "px-1.5",
      ].join(" ")}
    >
      {renaming ? (
        <div className="flex w-full items-center gap-2 py-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <InlineRename
            testId={`conv-rename-input-${conv.id}`}
            value={conv.title}
            onCommit={(next) => { onRename(conv.id, next); setRenaming(false); }}
            onCancel={() => setRenaming(false)}
          />
        </div>
      ) : (
        <>
          <button
            data-testid={`chat-row-${conv.id}`}
            onClick={() => onPick(conv.id)}
            onDoubleClick={() => setRenaming(true)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 text-left text-[12.5px]"
          >
            <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
            <span className="truncate">{conv.title}</span>
          </button>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`chat-menu-${conv.id}`}
                onClick={(e) => e.stopPropagation()}
                className={[
                  "ml-1 rounded p-1 text-muted-foreground transition-opacity hover:bg-background hover:text-foreground",
                  menuOpen ? "opacity-100" : "opacity-0 group-hover/row:opacity-100",
                ].join(" ")}
                aria-label="Conversation actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                data-testid={`chat-rename-${conv.id}`}
                onClick={() => setRenaming(true)}
                className="text-[12.5px]"
              >
                <Pencil className="h-3.5 w-3.5" /> Rename
              </DropdownMenuItem>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-[12.5px]" data-testid={`chat-move-${conv.id}`}>
                  <FolderInput className="h-3.5 w-3.5" /> Move to project
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-60 w-52 overflow-y-auto">
                    {projects.length === 0 && (
                      <div className="px-2 py-1.5 text-[12px] text-muted-foreground">
                        No projects yet
                      </div>
                    )}
                    {projects.map((p) => (
                      <DropdownMenuItem
                        key={p.id}
                        data-testid={`chat-move-target-${p.id}-${conv.id}`}
                        onClick={() => onMove(conv.id, p.id)}
                        className="text-[12.5px]"
                        disabled={conv.project_id === p.id}
                      >
                        <span className="truncate">{p.name}</span>
                      </DropdownMenuItem>
                    ))}
                    {conv.project_id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`chat-unassign-${conv.id}`}
                          onClick={() => onMove(conv.id, null)}
                          className="text-[12.5px] text-muted-foreground"
                        >
                          Remove from project
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              <DropdownMenuItem disabled className="text-[12.5px] opacity-60">
                <Copy className="h-3.5 w-3.5" /> Duplicate <span className="ml-auto text-[10px]">soon</span>
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="text-[12.5px] opacity-60">
                <Archive className="h-3.5 w-3.5" /> Archive <span className="ml-auto text-[10px]">soon</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                data-testid={`chat-delete-${conv.id}`}
                onClick={() => onDelete(conv.id)}
                className="text-[12.5px] text-rose-400 focus:text-rose-300"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}
